import { API_BASE, ApiError, loadStoredToken, newCorrelationId } from "./client";

/**
 * Lire un flux d'événements de l'assistant (V1.5-04).
 *
 * `fetch` plutôt qu'`EventSource` : la question part en `POST`, et `EventSource`
 * ne sait faire que des `GET` — il faudrait couper l'envoi et l'écoute en deux
 * appels, donc risquer qu'un rechargement relance l'orchestration. Un envoi,
 * un flux.
 *
 * En échange, la reconnexion est à notre charge, et c'est très bien : on veut
 * décider nous-mêmes s'il faut se rebrancher sur l'exécution ou simplement
 * relire la conversation, où la réponse est de toute façon persistée.
 */

export interface StreamEvent {
  version: string;
  event_id: string;
  sequence: number;
  type: string;
  timestamp: string;
  run_id: string;
  conversation_id: string;
  /** Nul pour une transcription : elle précède le message (V1.5-10). */
  user_message_id: string | null;
  /** Renseigné pour une transcription : de quelle pièce jointe on parle. */
  attachment_id?: string | null;
  assistant_message_id: string | null;
  payload: Record<string, unknown>;
}

/** Les types que l'interface traite. Le reste est ignoré sans bruit. */
export const EVENT = {
  accepted: "message.accepted",
  started: "orchestration.started",
  task: "task.extracted",
  attachment: "attachment.accepted",
  visionStarted: "vision.started",
  barcodeCompleted: "barcode.completed",
  ocrCompleted: "ocr.completed",
  visionCompleted: "vision.completed",
  matchingStarted: "matching.started",
  matchingCompleted: "matching.completed",
  /** L'entrée vocale (V1.5-10 §18). Quatre événements bornés : l'enregistrement
   *  est reçu, la transcription commence, elle finit ou elle échoue. Ce qui suit
   *  est le flux normal d'une question tapée — il n'est pas dupliqué. */
  audioAccepted: "audio.accepted",
  transcriptionStarted: "transcription.started",
  transcriptionCompleted: "transcription.completed",
  transcriptionFailed: "transcription.failed",
  /** La consultation des documents déposés (V1.5-09 §41). Deux événements et
   *  pas un par page : l'écran a besoin de savoir qu'on consulte les documents
   *  et combien d'extraits en sont sortis, pas de recevoir cent lignes. */
  documentSearchStarted: "document.search.started",
  documentSearchCompleted: "document.search.completed",
  clarification: "clarification.required",
  toolStarted: "tool.started",
  toolCompleted: "tool.completed",
  toolFailed: "tool.failed",
  evidence: "evidence.updated",
  answerStarted: "answer.started",
  answerCompleted: "answer.completed",
  completed: "orchestration.completed",
  failed: "orchestration.failed",
} as const;

function parseBlock(block: string): StreamEvent | null {
  const dataLine = block.split("\n").find((line) => line.startsWith("data: "));
  if (!dataLine) return null;
  try {
    return JSON.parse(dataLine.slice(6)) as StreamEvent;
  } catch {
    // Un événement illisible est ignoré : le flux continue, et la réponse
    // finale est en base de toute façon.
    return null;
  }
}

async function* readEvents(response: Response): AsyncGenerator<StreamEvent> {
  const reader = response.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Les événements SSE sont séparés par une ligne vide.
    let split = buffer.indexOf("\n\n");
    while (split !== -1) {
      const block = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      const event = parseBlock(block);
      if (event) yield event;
      split = buffer.indexOf("\n\n");
    }
  }
}

function headers(): Record<string, string> {
  const token = loadStoredToken();
  return {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    "X-Correlation-ID": newCorrelationId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Envoyer une question et suivre sa réponse. Un seul appel. */
export async function* streamMessage(
  conversationId: string,
  body: { content: string; client_message_id: string; attachment_ids?: string[] },
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const response = await fetch(
    `${API_BASE}/assistant/conversations/${conversationId}/messages/stream`,
    { method: "POST", headers: headers(), body: JSON.stringify(body), signal },
  );
  if (!response.ok) {
    throw new ApiError(response.status, "stream_failed", "HD-Vault n'a pas pu répondre.");
  }
  yield* readEvents(response);
}

/**
 * Suivre la transcription d'un enregistrement, ou s'y rebrancher (V1.5-10).
 *
 * Le même contrat que le flux d'une réponse, et la même règle : rien ici ne
 * lance de transcription. Une exécution partie dans l'autre travailleur n'est
 * pas dans ce tampon — le serveur attend alors l'état en base, qui est
 * l'endroit où elle finira de toute façon.
 */
export async function* streamTranscription(
  conversationId: string,
  attachmentId: string,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const response = await fetch(
    `${API_BASE}/assistant/conversations/${conversationId}/audio/${attachmentId}/events`,
    { method: "GET", headers: headers(), signal },
  );
  if (!response.ok) {
    throw new ApiError(response.status, "transcription_stream_failed", "La transcription n'a pas pu être suivie.");
  }
  yield* readEvents(response);
}

/**
 * Se rebrancher sur une exécution en cours, sans la relancer.
 *
 * `Last-Event-ID` dit où on s'était arrêté : le serveur rejoue la suite. Une
 * exécution qu'il ne connaît plus rend `404`, et l'appelant relit la
 * conversation — la réponse y est.
 */
export async function* resumeStream(
  conversationId: string,
  runId: string,
  lastEventId: string | null,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const response = await fetch(
    `${API_BASE}/assistant/conversations/${conversationId}/runs/${runId}/events`,
    {
      method: "GET",
      headers: { ...headers(), ...(lastEventId ? { "Last-Event-ID": lastEventId } : {}) },
      signal,
    },
  );
  if (!response.ok) {
    throw new ApiError(response.status, "resume_failed", "Cette exécution n'est plus suivie.");
  }
  yield* readEvents(response);
}
